import { useEffect, useMemo } from 'react';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogHeader,
    DialogFooter,
} from '@/components/ui/Dialog';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { SelectField } from '@/features/clients/SelectField';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useClientsList } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import {
    useActiveSessionTypes,
    useActiveLocationTypes,
} from '@/api/referenceData';
import { useSettings } from '@/api/settings';
import {
    useCreateSession,
    useCreateSeries,
    type SessionRecord,
} from '@/api/sessions';
import {
    generateSeriesDates,
    toIsoDate,
    type RecurrenceSpec,
} from '@/lib/calendar';
import { checkAvailability } from '@/lib/availability';
import { cn } from '@/lib/cn';

/*
 * SessionDialog — create-only modal for single sessions and series.
 *
 * Scope split (2026-04-19): this dialog creates. SessionPage handles
 * read/update/delete and the one-off → series promote flow. Putting edit
 * on a page opens room for the session-note tab + data-collection tab
 * without cramming three destinations into one modal.
 *
 * Constraints kept from the unified dialog:
 *  - Recurrence patterns are Weekly (every N weeks) or Daily (explicit set
 *    of weekdays). Saturday/Sunday must be selectable — the legacy app
 *    didn't allow them in its daily generator.
 *  - Fields use start/end times, not start+duration — duration is computed
 *    on submit and the DB still stores `dur`.
 */

/** 0=Sun … 6=Sat — matches JS Date.getDay() and the RecurrenceSpec contract. */
const WEEKDAY_TOGGLES: { value: number; short: string }[] = [
    { value: 0, short: 'Sun' },
    { value: 1, short: 'Mon' },
    { value: 2, short: 'Tue' },
    { value: 3, short: 'Wed' },
    { value: 4, short: 'Thu' },
    { value: 5, short: 'Fri' },
    { value: 6, short: 'Sat' },
];

const schema = z.object({
    // Admin session types (trainings, meetings) don't have a client, so the
    // schema can't require it here. The component enforces "required unless
    // admin" in the submit handler using the selected type's category.
    clientId:          z.string().optional(),
    staffId:           z.string().min(1, 'Staff is required'),
    serviceTypeId:     z.string().min(1, 'Session type is required'),
    date:              z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date is required'),
    startTime:         z.string().regex(/^\d{2}:\d{2}$/, 'Start time is required'),
    endTime:           z.string().regex(/^\d{2}:\d{2}$/, 'End time is required'),
    location:          z.string().optional(),
    label:             z.string().optional(),
    notes:             z.string().optional(),
    isSupervised:      z.boolean(),
    supervisingBcbaId: z.string().optional(),

    recurring:         z.boolean(),
    recurrenceType:    z.enum(['weekly', 'daily']),
    weekInterval:      z.number().int().min(1).max(12),
    daysOfWeek:        z.array(z.number().int().min(0).max(6)),
    recurrenceEndDate: z.string().optional(),
})
    .refine(
        (v) => timeToMinutes(v.endTime) > timeToMinutes(v.startTime),
        { message: 'End time must be after start time.', path: ['endTime'] },
    )
    .refine(
        (v) => !v.isSupervised || !!v.supervisingBcbaId,
        { message: 'Supervising BCBA is required when supervised.', path: ['supervisingBcbaId'] },
    )
    .refine(
        (v) => !v.recurring || (v.recurrenceEndDate && /^\d{4}-\d{2}-\d{2}$/.test(v.recurrenceEndDate)),
        { message: 'End date is required for recurring sessions.', path: ['recurrenceEndDate'] },
    )
    .refine(
        (v) => !v.recurring || !v.recurrenceEndDate || v.recurrenceEndDate >= v.date,
        { message: 'End date must be on or after the start date.', path: ['recurrenceEndDate'] },
    )
    .refine(
        (v) => !v.recurring || v.recurrenceType !== 'daily' || v.daysOfWeek.length > 0,
        { message: 'Pick at least one day.', path: ['daysOfWeek'] },
    );

type FormValues = z.infer<typeof schema>;

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

function formatTime12(hhmm: string): string {
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const suffix = h >= 12 ? 'pm' : 'am';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${pad(m)} ${suffix}`;
}

function timeToMinutes(t: string): number {
    const [h, m] = t.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

function minutesToTime(total: number): string {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${pad(h)}:${pad(m)}`;
}

function sessionDateMinIso(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return toIsoDate(d);
}

function sessionDateMaxIso(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return toIsoDate(d);
}

function formatDuration(minutes: number): string {
    if (minutes <= 0) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    return parts.join(' ');
}

export interface SessionDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Grid-click prefill. */
    defaultDate?: string;
    defaultHour?: number;
    defaultMinute?: number;
}

export function SessionDialog({
    open, onOpenChange,
    defaultDate, defaultHour, defaultMinute,
}: SessionDialogProps) {
    const clients = useClientsList();
    const staff = useUserDirectory();
    // BCBAs are derived from the full directory rather than fetched separately
    // — the directory entries already carry roleType, so a second filtered
    // request is wasted bandwidth + an extra audit row on every dialog mount.
    const bcbas = useMemo(
        () => (staff.data ?? []).filter((u) => u.roleType === 'BCBA'),
        [staff.data],
    );
    const sessionTypes = useActiveSessionTypes();
    const locations = useActiveLocationTypes();
    const settings = useSettings();
    const queryClient = useQueryClient();

    const createMut = useCreateSession();
    const createSeriesMut = useCreateSeries();

    useEffect(() => {
        if (!open) return;
        createMut.reset();
        createSeriesMut.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const activeClients = useMemo(
        () => (clients.data ?? [])
            .filter((c) => c.status === 'Active')
            .sort((a, b) => `${a.first} ${a.last}`.localeCompare(`${b.first} ${b.last}`)),
        [clients.data],
    );

    const defaults = useMemo<FormValues>(() => {
        const dh = defaultHour ?? 9;
        const dm = defaultMinute ?? 0;
        return {
            clientId: '', staffId: '', serviceTypeId: '',
            date: defaultDate ?? '',
            startTime: `${pad(dh)}:${pad(dm)}`,
            endTime: minutesToTime(dh * 60 + dm + 60),
            location: '', label: '', notes: '',
            isSupervised: false,
            supervisingBcbaId: '',
            recurring: false,
            recurrenceType: 'weekly',
            weekInterval: 1,
            daysOfWeek: [],
            recurrenceEndDate: '',
        };
    }, [defaultDate, defaultHour, defaultMinute]);

    const {
        register, handleSubmit, reset, watch, setValue, setError, control,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(schema) as Resolver<FormValues>,
        defaultValues: defaults,
    });

    useEffect(() => {
        if (open) reset(defaults);
    }, [open, defaults, reset]);

    const clientId           = watch('clientId');
    const staffId            = watch('staffId');
    const selectedType       = watch('serviceTypeId');
    const isSupervised       = watch('isSupervised');
    const supervisingBcbaId  = watch('supervisingBcbaId');
    const labelValue         = watch('label');
    const startTime          = watch('startTime');
    const endTime            = watch('endTime');
    const recurring          = watch('recurring');
    const recurrenceType     = watch('recurrenceType');
    const weekInterval       = watch('weekInterval');
    const daysOfWeek         = watch('daysOfWeek');
    const dateValue          = watch('date');
    const recurrenceEndDate  = watch('recurrenceEndDate');

    /*
     * Auto-fill the label with the session type's default when the user
     * picks a type and hasn't typed a custom label.
     */
    useEffect(() => {
        if (!selectedType) return;
        const t = sessionTypes.data?.find((st) => st.id === selectedType);
        if (t && !labelValue) {
            setValue('label', t.labelDefault || t.name, { shouldDirty: false });
        }
    }, [selectedType, sessionTypes.data, labelValue, setValue]);

    /*
     * Admin session types (trainings, meetings) aren't tied to a client or a
     * location. When the user switches to one, clear those fields.
     */
    const isAdminType = useMemo(() => {
        const t = sessionTypes.data?.find((st) => st.id === selectedType);
        return t?.category === 'Administrative';
    }, [selectedType, sessionTypes.data]);

    useEffect(() => {
        if (isAdminType) {
            setValue('clientId', '', { shouldDirty: true });
            setValue('location', '', { shouldDirty: true });
        }
    }, [isAdminType, setValue]);

    const spec: RecurrenceSpec = useMemo(
        () =>
            recurrenceType === 'weekly'
                ? { type: 'weekly', weekInterval: Math.max(1, Math.floor(weekInterval || 1)) }
                : { type: 'daily', daysOfWeek: daysOfWeek ?? [] },
        [recurrenceType, weekInterval, daysOfWeek],
    );

    const occurrences = useMemo(() => {
        if (!recurring) return [];
        if (!dateValue || !recurrenceEndDate) return [];
        return generateSeriesDates(dateValue, recurrenceEndDate, spec);
    }, [recurring, dateValue, recurrenceEndDate, spec]);

    const durMinutes = useMemo(
        () => Math.max(0, timeToMinutes(endTime || '00:00') - timeToMinutes(startTime || '00:00')),
        [startTime, endTime],
    );
    const seriesHours = (occurrences.length * durMinutes) / 60;

    const selectedClient = useMemo(
        () => activeClients.find((c) => c.id === clientId),
        [activeClients, clientId],
    );
    const remainingHours = selectedClient?.auth
        ? Math.max(0, selectedClient.auth.hours - selectedClient.auth.used)
        : null;
    const exceedsAuth = recurring && remainingHours !== null && seriesHours > remainingHours;
    const authBlock = settings.data?.billing?.authBlock ?? 'soft';
    const hardBlock = exceedsAuth && authBlock === 'hard';

    // Cached-sessions snapshot for conflict warnings. Read at open so the
    // dialog doesn't churn as the user edits.
    const allCachedSessions = useMemo(() => {
        if (!open) return [];
        const buckets = queryClient.getQueriesData<SessionRecord[]>({ queryKey: ['sessions'] });
        const seen = new Map<string, SessionRecord>();
        for (const [, rows] of buckets) {
            if (!rows) continue;
            for (const row of rows) seen.set(row.id, row);
        }
        return Array.from(seen.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryClient, open]);

    /*
     * Conflict + availability warnings — informational only (practice
     * fine-tuning decision 2026-04-18). Only the first occurrence is
     * checked for recurring; the user reviews the calendar for the rest.
     * Availability warnings fire only when the selected party's record
     * has at least one day configured — an empty record is treated as
     * "not set" and suppresses the warning.
     */
    const conflictWarnings = useMemo<string[]>(() => {
        if (!dateValue || !startTime || !endTime) return [];
        const newStart = timeToMinutes(startTime);
        const newEnd   = timeToMinutes(endTime);
        if (newEnd <= newStart) return [];

        const candidates = allCachedSessions.filter((s) => {
            if (!s.date || s.date !== dateValue) return false;
            if (s.status === 'Canceled') return false;
            const sStart = s.startH * 60 + s.startM;
            const sEnd   = sStart + s.dur;
            return newStart < sEnd && sStart < newEnd;
        });

        const msgs: string[] = [];
        const selectedStaff = staff.data?.find((u) => u.id === staffId);
        const selectedBcba  = isSupervised && supervisingBcbaId
            ? bcbas.find((u) => u.id === supervisingBcbaId)
            : null;

        if (staffId) {
            const hit = candidates.find((s) => s.staffId === staffId);
            if (hit) {
                const at = formatTime12(`${pad(hit.startH)}:${pad(hit.startM)}`);
                msgs.push(`Staff already has a session at ${at} with ${hit.client || 'another client'}.`);
            }
            if (selectedStaff) {
                const r = checkAvailability(selectedStaff.availability, dateValue, startTime, endTime);
                if (r.kind === 'outside') {
                    const name = `${selectedStaff.first} ${selectedStaff.last}`.trim() || 'Staff';
                    msgs.push(`${name} is unavailable ${r.dayLabel} from ${formatTime12(startTime)}–${formatTime12(endTime)}.`);
                }
            }
        }
        if (clientId) {
            const hit = candidates.find((s) => s.clientId === clientId);
            if (hit) {
                const at = formatTime12(`${pad(hit.startH)}:${pad(hit.startM)}`);
                msgs.push(`Client already has a session at ${at} with ${hit.staff || 'another staff member'}.`);
            }
            if (selectedClient) {
                const r = checkAvailability(selectedClient.availability, dateValue, startTime, endTime);
                if (r.kind === 'outside') {
                    const name = `${selectedClient.first} ${selectedClient.last}`.trim() || 'Client';
                    msgs.push(`${name} is unavailable ${r.dayLabel} from ${formatTime12(startTime)}–${formatTime12(endTime)}.`);
                }
            }
        }
        if (selectedBcba) {
            const r = checkAvailability(selectedBcba.availability, dateValue, startTime, endTime);
            if (r.kind === 'outside') {
                const name = `${selectedBcba.first} ${selectedBcba.last}`.trim() || 'Supervising BCBA';
                msgs.push(`Supervising BCBA ${name} is unavailable ${r.dayLabel} from ${formatTime12(startTime)}–${formatTime12(endTime)}.`);
            }
        }
        return msgs;
    }, [
        dateValue, startTime, endTime, staffId, clientId,
        isSupervised, supervisingBcbaId,
        allCachedSessions, staff.data, bcbas, selectedClient,
    ]);

    async function onSubmit(values: FormValues) {
        if (hardBlock) return;
        if (!isAdminType && !values.clientId) {
            setError('clientId', { type: 'manual', message: 'Client is required' });
            return;
        }
        const [sh, sm] = values.startTime.split(':').map(Number);
        const dur = Math.max(15, timeToMinutes(values.endTime) - timeToMinutes(values.startTime));
        const labelOut = values.label || '';
        const locationOut = values.location || '';
        const notesOut = values.notes || '';
        const supBcba = values.isSupervised ? (values.supervisingBcbaId || null) : null;

        try {
            if (values.recurring) {
                if (occurrences.length === 0) return;
                await createSeriesMut.mutateAsync({
                    clientId:           values.clientId,
                    staffId:            values.staffId,
                    serviceTypeId:      values.serviceTypeId,
                    startH:             sh,
                    startM:             sm,
                    dur,
                    label:              labelOut,
                    location:           locationOut,
                    notes:              notesOut,
                    patternType:        spec.type,
                    weekInterval:       spec.type === 'weekly' ? spec.weekInterval : undefined,
                    daysOfWeek:         spec.type === 'daily' ? spec.daysOfWeek : undefined,
                    isSupervised:       values.isSupervised,
                    supervisingBcbaId:  supBcba,
                    dates:              occurrences,
                });
            } else {
                await createMut.mutateAsync({
                    clientId:           values.clientId,
                    staffId:            values.staffId,
                    serviceTypeId:      values.serviceTypeId,
                    date:               values.date,
                    startH:             sh,
                    startM:             sm,
                    dur,
                    label:              labelOut,
                    location:           locationOut,
                    notes:               notesOut,
                    isSupervised:       values.isSupervised,
                    supervisingBcbaId:  supBcba,
                    status:             'Scheduled',
                });
            }
            onOpenChange(false);
        } catch {
            /* surfaced below via mutation error */
        }
    }

    const serverError = (() => {
        const e = createMut.error ?? createSeriesMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) {
            return e.response?.data?.error ?? 'Failed to save session';
        }
        return 'Unexpected error';
    })();

    const headerTitle = recurring ? 'Schedule Series' : 'Schedule Session';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="w-[min(calc(100vw-2rem),44rem)] p-0 overflow-hidden flex flex-col h-[min(calc(100vh-2rem),42rem)]"
                aria-describedby={undefined}
            >
                <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col flex-1 min-h-0">
                    <div className="px-5 pt-5 pb-0 shrink-0">
                        <DialogHeader className="mb-3">
                            <DialogTitle>{headerTitle}</DialogTitle>
                        </DialogHeader>
                    </div>

                    <div className="px-5 pb-5 pt-4 flex-1 min-h-0 overflow-auto">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0">
                            <div className="col-span-2 mb-3">
                                <span className="text-2xs font-bold uppercase tracking-wider text-text-subtle">Details</span>
                            </div>

                            <FormField
                                label="Session type"
                                required
                                error={errors.serviceTypeId?.message}
                                className="col-span-2"
                            >
                                {(id) => (
                                    <SelectField id={id} {...register('serviceTypeId')}>
                                        <option value="">Select type…</option>
                                        {(sessionTypes.data ?? []).map((t) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </SelectField>
                                )}
                            </FormField>

                            {!isAdminType && (
                                <>
                                    <FormField label="Client" required error={errors.clientId?.message}>
                                        {(id) => (
                                            <SelectField id={id} {...register('clientId')}>
                                                <option value="">Select client…</option>
                                                {activeClients.map((c) => (
                                                    <option key={c.id} value={c.id}>{c.first} {c.last}</option>
                                                ))}
                                            </SelectField>
                                        )}
                                    </FormField>

                                    <FormField label="Location" error={errors.location?.message}>
                                        {(id) => (
                                            <SelectField id={id} {...register('location')}>
                                                <option value="">—</option>
                                                {(locations.data ?? []).map((l) => (
                                                    <option key={l.id} value={l.name}>{l.name}</option>
                                                ))}
                                            </SelectField>
                                        )}
                                    </FormField>
                                </>
                            )}

                            <FormField label="Staff" required error={errors.staffId?.message}>
                                {(id) => (
                                    <SelectField id={id} {...register('staffId')}>
                                        <option value="">Select staff…</option>
                                        {(staff.data ?? []).map((u) => (
                                            <option key={u.id} value={u.id}>
                                                {u.first} {u.last} · {u.roleName}
                                            </option>
                                        ))}
                                    </SelectField>
                                )}
                            </FormField>

                            <FormField
                                label="Label"
                                hint={!labelValue && selectedType ? 'Defaults to session type name.' : undefined}
                                error={errors.label?.message}
                            >
                                {(id) => (
                                    <Input id={id} {...register('label')} />
                                )}
                            </FormField>

                            <div className="col-span-2 mb-3 mt-1 pt-3 border-t border-border">
                                <span className="text-2xs font-bold uppercase tracking-wider text-text-subtle">Schedule</span>
                            </div>

                            <FormField label="Date" required error={errors.date?.message}>
                                {(id) => (
                                    <Input id={id} type="date" min={sessionDateMinIso()} max={sessionDateMaxIso()} {...register('date')} />
                                )}
                            </FormField>
                            <div />

                            <FormField label="Start time" required error={errors.startTime?.message}>
                                {(id) => (
                                    <Input id={id} type="time" step={900} {...register('startTime')} />
                                )}
                            </FormField>

                            <FormField
                                label="End time"
                                required
                                error={errors.endTime?.message}
                                hint={formatDuration(durMinutes) ? `Duration: ${formatDuration(durMinutes)}` : undefined}
                            >
                                {(id) => (
                                    <Input id={id} type="time" step={900} {...register('endTime')} />
                                )}
                            </FormField>

                            <div className="col-span-1 mb-3.5 flex items-center">
                                <label className="flex items-center gap-2 text-sm text-text">
                                    <input type="checkbox" {...register('recurring')} />
                                    Recurring session
                                </label>
                            </div>
                            <div />

                            {recurring && (
                                <>
                                    <FormField label="Recurrence" required>
                                        {(id) => (
                                            <SelectField id={id} {...register('recurrenceType')}>
                                                <option value="weekly">Weekly</option>
                                                <option value="daily">Daily</option>
                                            </SelectField>
                                        )}
                                    </FormField>

                                    {recurrenceType === 'weekly' ? (
                                        <FormField label="Every (weeks)" error={errors.weekInterval?.message}>
                                            {(id) => (
                                                <Input
                                                    id={id}
                                                    type="number"
                                                    step={1}
                                                    min={1}
                                                    max={12}
                                                    {...register('weekInterval', { valueAsNumber: true })}
                                                />
                                            )}
                                        </FormField>
                                    ) : (
                                        <FormField label="Days of week" error={errors.daysOfWeek?.message as string | undefined}>
                                            {() => (
                                                <Controller
                                                    control={control}
                                                    name="daysOfWeek"
                                                    render={({ field }) => (
                                                        <div className="flex flex-wrap gap-1">
                                                            {WEEKDAY_TOGGLES.map((d) => {
                                                                const checked = field.value?.includes(d.value);
                                                                return (
                                                                    <button
                                                                        key={d.value}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const next = checked
                                                                                ? (field.value ?? []).filter((v) => v !== d.value)
                                                                                : [...(field.value ?? []), d.value];
                                                                            field.onChange(next);
                                                                        }}
                                                                        className={cn(
                                                                            'px-2 py-1 rounded-md text-xs border transition-colors',
                                                                            checked
                                                                                ? 'bg-accent text-[#111] border-accent'
                                                                                : 'bg-background text-text-muted border-border hover:text-text',
                                                                        )}
                                                                    >
                                                                        {d.short}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                />
                                            )}
                                        </FormField>
                                    )}

                                    <FormField label="Starts" hint="Uses the Date field above.">
                                        {(id) => (
                                            <Input id={id} type="date" value={dateValue} disabled readOnly />
                                        )}
                                    </FormField>
                                    <FormField
                                        label="Ends"
                                        required
                                        error={errors.recurrenceEndDate?.message}
                                    >
                                        {(id) => (
                                            <Input id={id} type="date" min={dateValue || sessionDateMinIso()} max={sessionDateMaxIso()} {...register('recurrenceEndDate')} />
                                        )}
                                    </FormField>
                                </>
                            )}

                            {(recurring || exceedsAuth || conflictWarnings.length > 0) && (
                                <div className="col-span-2 mb-3.5 space-y-2">
                                    {recurring && (
                                        <Alert variant="info">
                                            {occurrences.length === 0
                                                ? 'No occurrences match this pattern + date range.'
                                                : `${occurrences.length} occurrence${occurrences.length === 1 ? '' : 's'} · ${seriesHours.toFixed(2)} total hours.`}
                                        </Alert>
                                    )}
                                    {exceedsAuth && (
                                        <Alert variant={hardBlock ? 'danger' : 'warning'}>
                                            {hardBlock
                                                ? `Series ${seriesHours.toFixed(2)}h exceeds remaining auth (${remainingHours?.toFixed(2)}h). Blocked by practice setting.`
                                                : `Series ${seriesHours.toFixed(2)}h exceeds remaining auth (${remainingHours?.toFixed(2)}h). Proceed only if you plan to extend the authorization.`}
                                        </Alert>
                                    )}
                                    {conflictWarnings.map((msg, i) => (
                                        <Alert key={`conflict-${i}`} variant="warning">{msg}</Alert>
                                    ))}
                                </div>
                            )}

                            <div className="col-span-2 mb-3 mt-1 pt-3 border-t border-border">
                                <span className="text-2xs font-bold uppercase tracking-wider text-text-subtle">Additional information</span>
                            </div>

                            <FormField label="Notes" error={errors.notes?.message} className="col-span-2">
                                {(id) => (
                                    <Textarea id={id} rows={2} {...register('notes')} />
                                )}
                            </FormField>

                            <div className="col-span-2 mb-3.5 flex items-center">
                                <label className="flex items-center gap-2 text-sm text-text">
                                    <input type="checkbox" {...register('isSupervised')} />
                                    Supervised session
                                </label>
                            </div>

                            {isSupervised && (
                                <FormField
                                    label="Supervising BCBA"
                                    required
                                    error={errors.supervisingBcbaId?.message}
                                    className="col-span-2"
                                >
                                    {(id) => (
                                        <SelectField id={id} {...register('supervisingBcbaId')}>
                                            <option value="">Select BCBA…</option>
                                            {bcbas.map((u) => (
                                                <option key={u.id} value={u.id}>
                                                    {u.first} {u.last}
                                                </option>
                                            ))}
                                        </SelectField>
                                    )}
                                </FormField>
                            )}
                        </div>

                        {serverError && (
                            <div className="mt-2">
                                <Alert variant="danger">{serverError}</Alert>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="mx-5 mb-5 mt-0 shrink-0">
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="success"
                            disabled={isSubmitting || hardBlock || (recurring && occurrences.length === 0)}
                        >
                            {isSubmitting
                                ? 'Saving…'
                                : recurring
                                    ? `Schedule ${occurrences.length || ''}`.trim()
                                    : 'Schedule'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
